<template>
  <div>
    <div class="an-verdict">
      <span class="tf-badge" :class="ratingBadge[verdict.rating]">{{ ratingText[verdict.rating] }}</span>
      <span>{{ verdict.text }}</span>
    </div>

    <div v-if="data.lowData" class="tf-alert tf-alert--info">
      <i class="fa fa-info-circle tf-alert__icon"></i>
      <div class="tf-alert__body">
        <div class="tf-alert__title">Замеров пока мало — цифры могут сильно скакать</div>
      </div>
    </div>

    <div class="an-tiles">
      <div v-for="tile in metricTiles" :key="tile.metric" class="an-tile" :title="tile.hint">
        <div class="an-tile__top">
          <span class="an-tile__title">{{ tile.name }}</span>
        </div>
        <div class="an-tile__value">
          {{ tile.value }}
          <span v-if="tile.rating" class="tf-badge" :class="ratingBadge[tile.rating]">{{ ratingText[tile.rating] }}</span>
          <span v-if="tile.change" class="an-change" :class="'an-change--' + tile.change.direction">
            {{ tile.change.text }}
          </span>
        </div>
        <RumShareBar v-bind="tile.shares"/>
        <div class="an-tile__sub">так или быстрее — у 3 из 4 посетителей · {{ formatCount(tile.count) }} замеров</div>
      </div>

      <div class="an-tile">
        <div class="an-tile__top"><span class="an-tile__title">Просмотры</span></div>
        <div class="an-tile__value">
          {{ formatCount(data.views) }}
          <span v-if="viewsChange" class="an-change">{{ viewsChange }}</span>
        </div>
        <div class="an-tile__sub">прошлый период: {{ formatCount(data.prevViews) }}</div>
      </div>

      <div class="an-tile">
        <div class="an-tile__top"><span class="an-tile__title">Отказы</span></div>
        <div class="an-tile__value">{{ formatPercent(data.bounceRate) }}</div>
        <div class="an-tile__sub">ушли, ничего не сделав на странице</div>
      </div>
    </div>

    <section v-if="data.release" class="tf-card">
      <div class="tf-card__head">
        <span class="tf-card__icon"><i class="fa fa-rocket"></i></span>
        <div class="tf-card__titles">
          <h3 class="tf-card__title">Последний релиз: {{ data.release.label }}</h3>
          <p class="tf-card__sub">по сравнению с предыдущим</p>
        </div>
        <span class="tf-badge" :class="releaseVerdict.badge">{{ releaseVerdict.text }}</span>
      </div>
    </section>

    <section class="tf-card">
      <div class="tf-card__head">
        <span class="tf-card__icon"><i class="fa fa-lightbulb-o"></i></span>
        <div class="tf-card__titles">
          <h3 class="tf-card__title">Что происходит</h3>
          <p class="tf-card__sub">Выводы по замерам, самое важное сверху</p>
        </div>
      </div>
      <div class="tf-card__body">
        <div v-if="!insights.length" class="an-empty">
          <i class="fa" :class="data.lowData ? 'fa-hourglass-half' : 'fa-check-circle'"></i>
          <span>{{ data.lowData ? 'Выводов пока нет: для них нужно больше замеров' : 'Ничего заметного не произошло' }}</span>
        </div>
        <div v-else class="an-insights">
          <article v-for="item in insights" :key="item.id" class="an-insight">
            <div class="an-insight__head">
              <span class="tf-badge" :class="severityBadge[item.severity]">{{ severityText[item.severity] }}</span>
              <span class="an-insight__title">{{ item.title }}</span>
            </div>
            <p class="an-insight__text">{{ item.text }}</p>
            <p v-if="item.cause" class="an-insight__cause">{{ item.cause }}</p>
            <template v-if="item.advice && item.advice.length">
              <button type="button" class="an-insight__toggle" @click="toggle(item.id)">
                {{ opened[item.id] ? 'Скрыть' : 'Что сделать' }}
              </button>
              <ul v-if="opened[item.id]" class="an-insight__advice">
                <li v-for="(line, i) in item.advice" :key="i">{{ line }}</li>
              </ul>
            </template>
          </article>
        </div>
      </div>
    </section>

    <section class="tf-card">
      <div class="tf-card__head">
        <span class="tf-card__icon"><i class="fa fa-line-chart"></i></span>
        <div class="tf-card__titles">
          <h3 class="tf-card__title">Динамика</h3>
          <p class="tf-card__sub">Время у 75% посетителей; стабильность вёрстки на графике не показана</p>
        </div>
      </div>
      <div class="tf-card__body">
        <div v-if="!chartSeries.some(s => s.data.some(v => v !== null))" class="an-empty">
          <i class="fa fa-line-chart"></i>
          <span>Точек для графика нет</span>
        </div>
        <apexchart v-else type="line" height="300" :key="'tl' + (isDark ? 'd' : 'l')"
                   :options="chartOptions" :series="chartSeries"/>
      </div>
    </section>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import RumShareBar from "./RumShareBar.vue";
import {
  RUM_METRICS,
  METRIC_TEXT,
  RATING_TEXT,
  RATING_BADGE,
  SEVERITY_TEXT,
  SEVERITY_BADGE,
  RELEASE_VERDICT,
  formatMetric,
  formatPercent,
  formatCount,
  formatChange,
  timelinePointMs,
} from "@/common/rumFormat";

const CHART_METRICS = ['LCP', 'INP', 'TTFB']

// Палитра дашборда поддержки: проверена на различимость при дальтонизме.
const CHART_COLORS = {
  light: ['#7366ff', '#199e70', '#d95926'],
  dark: ['#7366ff', '#1baf7a', '#d95926'],
}

export default {
  name: "RumOverview",
  components: {apexchart: VueApexCharts, RumShareBar},
  props: {
    data: {type: Object, required: true},
    isDark: {type: Boolean, default: false},
  },
  data() {
    return {
      opened: {},
      ratingText: RATING_TEXT,
      ratingBadge: RATING_BADGE,
      severityText: SEVERITY_TEXT,
      severityBadge: SEVERITY_BADGE,
    }
  },
  computed: {
    verdict() {
      return this.data.verdict || {rating: 'unknown', text: ''}
    },
    metricTiles() {
      return RUM_METRICS.map(metric => {
        const summary = (this.data.summary || {})[metric] || {}
        return {
          metric,
          name: METRIC_TEXT[metric].name,
          hint: METRIC_TEXT[metric].hint,
          value: formatMetric(metric, summary.p75),
          count: summary.count || 0,
          rating: (this.data.ratings || {})[metric] || null,
          change: formatChange((this.data.changes || {})[metric]),
          shares: {good: summary.good || 0, ni: summary.ni || 0, poor: summary.poor || 0},
        }
      })
    },
    viewsChange() {
      const {views, prevViews} = this.data
      if (!prevViews) return null
      const rel = (views - prevViews) / prevViews
      return `${rel > 0 ? '+' : ''}${Math.round(rel * 100)}%`
    },
    releaseVerdict() {
      return RELEASE_VERDICT[this.data.release?.verdict] || RELEASE_VERDICT['not-enough']
    },
    insights() {
      return this.data.insights || []
    },
    chartSeries() {
      const timeline = this.data.timeline || []
      const p75 = (point, metric) => point.metrics?.[metric]?.p75 ?? null
      // По суткам — ось категорий, одна подпись на день: на оси времени
      // ApexCharts при двух-трёх днях повторяет одну дату по всей оси.
      if (this.dailyTimeline) {
        return CHART_METRICS.map(metric => ({
          name: METRIC_TEXT[metric].name,
          data: timeline.map(point => p75(point, metric)),
        }))
      }
      return CHART_METRICS.map(metric => ({
        name: METRIC_TEXT[metric].name,
        data: timeline
            .filter(point => p75(point, metric) !== null)
            .map(point => [timelinePointMs(point.t), p75(point, metric)]),
      }))
    },
    dayLabels() {
      return (this.data.timeline || []).map(point => {
        const date = new Date(timelinePointMs(point.t))
        return date.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit'})
      })
    },
    dailyTimeline() {
      const first = (this.data.timeline || [])[0]
      return Boolean(first && first.t.length === 10)
    },
    chartPoints() {
      return Math.max(...this.chartSeries.map(s => s.data.length))
    },
    chartOptions() {
      const text = this.isDark ? '#98a6ad' : '#7b8496'
      return {
        chart: {toolbar: {show: false}, zoom: {enabled: false}, background: 'transparent', fontFamily: 'inherit'},
        theme: {mode: this.isDark ? 'dark' : 'light'},
        colors: CHART_COLORS[this.isDark ? 'dark' : 'light'],
        // Без сглаживания: при нескольких точках кривая рисовала бы плавный
        // тренд, которого в данных нет. Точки видны, пока их немного.
        stroke: {curve: 'straight', width: 2},
        markers: {size: this.chartPoints <= 48 ? 4 : 0, strokeWidth: 0},
        grid: {borderColor: this.isDark ? '#374558' : '#e7eaf3'},
        xaxis: this.dailyTimeline
            ? {type: 'category', categories: this.dayLabels, labels: {style: {colors: text}}}
            : {type: 'datetime', labels: {datetimeUTC: false, style: {colors: text}}},
        yaxis: {labels: {formatter: v => formatMetric('LCP', v), style: {colors: text}}},
        legend: {labels: {colors: text}},
        tooltip: {
          shared: true,
          intersect: false,
          theme: this.isDark ? 'dark' : 'light',
          x: this.dailyTimeline ? {} : {format: 'dd.MM HH:mm'},
        },
      }
    },
  },
  methods: {
    formatPercent,
    formatCount,
    toggle(id) {
      this.$set(this.opened, id, !this.opened[id])
    },
  },
}
</script>
